import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Stack, Skeleton, Alert, Chip,
  Button, alpha, useTheme,
} from '@mui/material';
import {
  ShoppingBag, Users as UsersIcon, Briefcase, MessageSquare,
  Layers, Tag, Globe, Heart, FileText, Database, MessageCircle, Sparkles, ArrowRight,
} from 'lucide-react';
import DashboardSectionHeader from '../components/DashboardSectionHeader';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import KpiTile from '../components/charts/KpiTile';
import AreaTrendCard from '../components/charts/AreaTrendCard';
import DonutCard from '../components/charts/DonutCard';
import HorizontalBarCard from '../components/charts/HorizontalBarCard';
import RecentActivityCard from '../components/charts/RecentActivityCard';
import ComparisonChart from '../components/ComparisonChart';
import { fetchAdvancedAnalytics, setWindowDays } from '../redux/slices/analyticsSlice';
import { fetchDashboardStats } from '../redux/slices/dashboardSlice';

const RANGES = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

/** Stretch grid children to equal height — Minimal dashboard pattern */
const gridItemSx = {
  display: 'flex',
  '& > *': { flex: 1, width: '100%', minWidth: 0 },
};

const HeroBanner = ({ user, windowLabel, onJump }) => {
  const theme = useTheme();
  const greetingName =
    user?.name ||
    user?.first_name ||
    'Admin';
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 3.5,
        overflow: 'hidden',
        width: '100%',
        p: { xs: 2.5, md: 3.5 },
        mb: 3,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, #0F172A 0%, #1E293B 60%, ${alpha(theme.palette.primary.darker, 0.85)} 100%)`
          : `linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #182234 100%)`,
        color: '#fff',
        border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.divider, 0.4)}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 10px 25px -5px rgba(0, 0, 0, 0.5)'
          : `0 12px 28px -6px rgba(15, 23, 42, 0.25)`,
      }}
    >
      {/* decorative blobs */}
      <Box
        sx={{
          position: 'absolute',
          right: -60,
          top: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: alpha('#fff', 0.10),
          filter: 'blur(2px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          right: 60,
          bottom: -100,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: alpha('#fff', 0.07),
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ position: 'relative' }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Chip
            size="small"
            icon={<Sparkles size={14} />}
            label={`Snapshot · ${windowLabel}`}
            sx={{
              bgcolor: alpha('#fff', 0.18),
              color: '#fff',
              fontWeight: 700,
              mb: 1.5,
              '& .MuiChip-icon': { color: '#fff' },
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: -0.6,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1.15,
            }}
          >
            Welcome back, {greetingName} 👋
          </Typography>
          <Typography sx={{ mt: 0.75, opacity: 0.88, fontSize: { xs: 13.5, md: 15 } }}>
            Here's the real-time pulse of your marketplace — listings, conversations,
            verified businesses and leads, all in one place.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          alignItems="stretch"
          sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
        >
          <Button
            onClick={() => onJump('/analytics')}
            sx={{
              bgcolor: '#fff',
              color: theme.palette.primary.main,
              fontWeight: 700,
              px: 3.5,
              height: 44,
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: 'nowrap',
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#fff',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-1px)',
                '& .arrow-icon': {
                  transform: 'translateX(4px)',
                },
              },
            }}
            endIcon={
              <ArrowRight
                className="arrow-icon"
                size={16}
                style={{ transition: 'transform 0.2s' }}
              />
            }
          >
            Open analytics
          </Button>
          <Button
            onClick={() => onJump('/wbr')}
            sx={{
              bgcolor: alpha('#fff', 0.12),
              color: '#fff',
              fontWeight: 700,
              px: 3.5,
              height: 44,
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: 'nowrap',
              borderRadius: '8px',
              textTransform: 'none',
              border: `1px solid ${alpha('#fff', 0.25)}`,
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: alpha('#fff', 0.2),
                borderColor: alpha('#fff', 0.4),
                transform: 'translateY(-1px)',
              },
            }}
          >
            View WBR report
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const seriesToSparks = (arr) => (arr || []).map((p) => ({ v: p.count }));

const DashboardView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, isLoading, windowDays } = useSelector((s) => s.analytics);
  const { stats } = useSelector((s) => s.dashboard);
  const { user } = useSelector((s) => s.auth) || {};
  const [activeTrend, setActiveTrend] = useState('products');

  useEffect(() => {
    dispatch(fetchAdvancedAnalytics(windowDays));
    dispatch(fetchDashboardStats());
  }, [dispatch, windowDays]);

  const totals = useMemo(() => data?.totals || {}, [data]);
  // Counts of records created within the selected time range — these are what the
  // "Key metrics" KPIs show so they respond to the 7d/30d/90d filter (the all-time
  // totals are only used for the static "Catalog at a glance" master-data counts).
  const windowCounts = useMemo(() => data?.window || {}, [data]);
  const growth = useMemo(() => data?.growth || {}, [data]);
  const series = useMemo(() => data?.series || {}, [data]);
  const breakdowns = data?.breakdowns || {};
  const recent = data?.recent || {};

  const kpis = useMemo(() => [
    { label: 'Products', value: fmt(windowCounts.products), icon: <ShoppingBag size={20} />, color: '#FE5F01', growth: growth.products, secondary: 'new listings', series: seriesToSparks(series.products) },
    { label: 'Users', value: fmt(windowCounts.users), icon: <UsersIcon size={20} />, color: '#A855F7', growth: growth.users, secondary: 'new signups', series: seriesToSparks(series.users) },
    { label: 'Businesses', value: fmt(windowCounts.businesses), icon: <Briefcase size={20} />, color: '#F59E0B', growth: growth.businesses, secondary: 'new storefronts', series: seriesToSparks(series.businesses) },
    // "Buyer Enquiries" = demand submitted. "Leads Delivered" = deliveries to
    // sellers. This tile used to read "Leads" while counting enquiries, which is
    // where the enquiry-vs-lead confusion came from.
    { label: 'Buyer Enquiries', value: fmt(windowCounts.enquiries), icon: <MessageSquare size={20} />, color: '#EF4444', growth: growth.enquiries, secondary: 'demand submitted', series: seriesToSparks(series.enquiries) },
  ], [windowCounts, growth, series]);

  const secondaryKpis = useMemo(() => [
    { label: 'Leads Delivered', value: fmt(windowCounts.leads), icon: <MessageSquare size={20} />, color: '#EF4444', growth: growth.leads, secondary: 'sent to sellers' },
    { label: 'Chats', value: fmt(windowCounts.conversations), icon: <MessageCircle size={20} />, color: '#3B82F6', secondary: 'new threads' },
    { label: 'RFQs', value: fmt(windowCounts.rfqs), icon: <FileText size={20} />, color: '#0EA5E9', secondary: 'new quote requests' },
    { label: 'Saved Searches', value: fmt(windowCounts.saved_searches), icon: <Heart size={20} />, color: '#EC4899', secondary: 'new alerts' },
    { label: 'Wishlists', value: fmt(windowCounts.wishlists), icon: <Heart size={20} />, color: '#10B981', secondary: 'newly saved' },
  ], [windowCounts, growth]);

  const catalogKpis = useMemo(() => [
    { label: 'Categories', value: fmt(totals.categories), icon: <Database size={18} />, color: '#FE5F01' },
    { label: 'Sub-categories', value: fmt(totals.sub_categories), icon: <Layers size={18} />, color: '#8B5CF6' },
    { label: 'Brands', value: fmt(totals.brands), icon: <Tag size={18} />, color: '#06B6D4' },
    { label: 'Cities', value: fmt(totals.cities), icon: <Globe size={18} />, color: '#14B8A6' },
    { label: 'States', value: fmt(totals.states), icon: <Globe size={18} />, color: '#84CC16' },
    { label: 'Plan Orders', value: fmt(totals.orders), icon: <ShoppingBag size={18} />, color: '#F97316' },
  ], [totals]);

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <HeroBanner
        user={user}
        windowLabel={`Last ${windowDays} days`}
        onJump={(path) => navigate(path)}
      />

      <DashboardSectionHeader
        title="Key metrics"
        subtitle={`New activity in the last ${windowDays} days`}
        chip={data?.isMock ? (
          <Chip size="small" label="Sample data" color="warning" sx={{ fontWeight: 700 }} />
        ) : null}
        ranges={RANGES}
        rangeValue={windowDays}
        onRangeChange={(v) => dispatch(setWindowDays(v))}
        onRefresh={() => dispatch(fetchAdvancedAnalytics(windowDays))}
      />

      {/* Primary KPI strip — 4 columns on desktop, 2x2 on mobile */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ width: '100%', mt: { xs: 0.5, md: 1 }, mb: 3 }}>
        {kpis.map((k) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={k.label} sx={gridItemSx}>
            {isLoading && !data ? (
              <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
            ) : (
              <KpiTile {...k} />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Secondary KPI strip */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2 }, mb: 3 }}>
        {secondaryKpis.map((k) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={k.label} sx={gridItemSx}>
            {isLoading && !data ? (
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            ) : (
              <KpiTile {...k} />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Trend + Donut */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 }, mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <AreaTrendCard
              title="Growth Trend"
              subtitle={`Last ${windowDays} days`}
              seriesMap={series}
              growth={growth}
              activeKey={activeTrend}
              onSelect={setActiveTrend}
              height={{ xs: 260, sm: 300, md: 340 }}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <DonutCard
              title="Products by Category"
              subtitle="Top 8 categories"
              data={(breakdowns.productsByCategory || []).slice(0, 8)}
              height={{ xs: 240, md: 280 }}
            />
          )}
        </Grid>
      </Grid>

      <DashboardSectionHeader
        title="Catalog at a glance"
        subtitle="Master data counts across your marketplace"
      />
      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ width: '100%', mt: { xs: 0.5, md: 1 }, mb: 3 }}>
        {catalogKpis.map((k) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label} sx={gridItemSx}>
            {isLoading && !data ? (
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: 3 }} />
            ) : (
              <KpiTile {...k} />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Geography + Recent */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 }, mb: 3 }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <HorizontalBarCard
              title="Top Cities"
              subtitle="By product listings"
              data={(breakdowns.productsByCity || []).slice(0, 8)}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <HorizontalBarCard
              title="Top States"
              subtitle="By product listings"
              data={(breakdowns.productsByState || []).slice(0, 8)}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <RecentActivityCard recent={recent} />
          )}
        </Grid>
      </Grid>

      {/* Comparison bar chart */}
      {stats?.comparisonData?.length > 0 && (
        <Grid container spacing={3} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 }, mb: 2 }}>
          <Grid size={12} sx={gridItemSx}>
            <ComparisonChart data={stats.comparisonData} />
          </Grid>
        </Grid>
      )}

      {!isLoading && !data && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Analytics service is unavailable — showing sample data.
        </Alert>
      )}
    </Box>
  );
};

export default DashboardView;
